import {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query"

import { FetchError } from "@medusajs/js-sdk"
import { HttpTypes } from "@medusajs/types"
import { sdk } from "../../lib/client"
import { queryClient } from "../../lib/query-client"
import { queryKeysFactory } from "../../lib/query-key-factory"
import { fulfillmentProvidersQueryKeys } from "./fulfillment-providers"

const STOCK_LOCATIONS_QUERY_KEY = "stock_locations" as const
export const stockLocationsQueryKeys = queryKeysFactory(
  STOCK_LOCATIONS_QUERY_KEY
)

export const useStockLocation = (
  id: string,
  query?: HttpTypes.SelectParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminStockLocationResponse,
      FetchError,
      HttpTypes.AdminStockLocationResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () => sdk.admin.stockLocation.retrieve(id, query),
    queryKey: stockLocationsQueryKeys.detail(id, query),
    ...options,
  })

  return { ...data, ...rest }
}

export const useStockLocations = (
  query?: HttpTypes.AdminStockLocationListParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminStockLocationListResponse,
      FetchError,
      HttpTypes.AdminStockLocationListResponse,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () => sdk.admin.stockLocation.list(query),
    queryKey: stockLocationsQueryKeys.list(query),
    ...options,
  })

  return { ...data, ...rest }
}

export const useAssignedStockLocations = (
  query?: Record<string, any>,
  options?: Omit<
    UseQueryOptions<any, FetchError, any, QueryKey>,
    "queryKey" | "queryFn"
  >
) => {
  const { data, ...rest } = useQuery({
    queryFn: () =>
      sdk.client.fetch<any>("/admin/stock-locations/assigned", {
        method: "GET",
        query,
      }),
    queryKey: [...stockLocationsQueryKeys.lists(), "assigned", query],
    ...options,
  })

  return {
    stock_locations: data?.stock_locations as any[] | undefined,
    count: data?.count as number | undefined,
    ...rest,
  }
}

export const useCreateStockLocation = (
  options?: UseMutationOptions<
    HttpTypes.AdminStockLocationResponse,
    FetchError,
    HttpTypes.AdminCreateStockLocation
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.stockLocation.create(payload),
    onSuccess: async (data, variables, context, _ctx) => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useUpdateStockLocation = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminStockLocationResponse,
    FetchError,
    HttpTypes.AdminUpdateStockLocation
  >
) => {
  return useMutation({
    mutationFn: (payload) => sdk.admin.stockLocation.update(id, payload),
    onSuccess: async (data, variables, context, _ctx) => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.details(),
      })
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useUpdateStockLocationSalesChannels = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminStockLocationResponse,
    FetchError,
    HttpTypes.AdminUpdateStockLocationSalesChannels
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.admin.stockLocation.updateSalesChannels(id, payload),
    onSuccess: async (data, variables, context, _ctx) => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.details(),
      })
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.lists(),
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useDeleteStockLocation = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminStockLocationDeleteResponse,
    FetchError,
    void
  >
) => {
  return useMutation({
    mutationFn: () => sdk.admin.stockLocation.delete(id),
    onSuccess: async (data, variables, context, _ctx) => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.lists(),
      })
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.detail(id),
      })
      await queryClient.invalidateQueries({
        queryKey: [...stockLocationsQueryKeys.all, "recently-deleted"],
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useCreateStockLocationFulfillmentSet = (
  locationId: string,
  options?: UseMutationOptions<
    HttpTypes.AdminStockLocationResponse,
    FetchError,
    HttpTypes.AdminCreateStockLocationFulfillmentSet
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      sdk.admin.stockLocation.createFulfillmentSet(locationId, payload),
    onSuccess: async (data, variables, context, _ctx) => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.all,
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}

export const useRepairFulfillment = (locationId: string) => {
  return useMutation({
    mutationFn: () =>
      sdk.client.fetch<{
        success: boolean
        created: {
          fulfillment_set: boolean
          service_zone: boolean
          shipping_option: boolean
        }
      }>(`/admin/stock-locations/${locationId}/repair-fulfillment`, {
        method: "POST",
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.details(),
      })
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.lists(),
      })
    },
  })
}

export type FacilityMergeCheckResponse = {
  source: { id: string; name: string }
  target: { id: string; name: string }
  seller_id: string
  assets: {
    source_inventory_levels: number
    target_inventory_levels: number
    source_fulfillment_sets: number
    source_has_profile: boolean
    target_has_profile: boolean
  }
  can_merge: boolean
  block_reasons: string[]
}

export const useFacilityMergeCheck = (
  sourceId: string,
  targetId: string,
  enabled: boolean
) => {
  return useQuery<FacilityMergeCheckResponse, FetchError, FacilityMergeCheckResponse>({
    queryKey: [...stockLocationsQueryKeys.all, "merge-check", sourceId, targetId],
    queryFn: () =>
      sdk.client.fetch(`/admin/stock-locations/${sourceId}/merge-check`, {
        method: "GET",
        query: { target_location_id: targetId },
      }),
    enabled: enabled && !!targetId,
    retry: false,
  })
}

export const useMergeFacility = () => {
  return useMutation({
    mutationFn: ({
      sourceId,
      targetId,
    }: {
      sourceId: string
      targetId: string
    }) =>
      sdk.client.fetch<{ success: boolean; transferred: Record<string, number> }>(
        `/admin/stock-locations/${sourceId}/merge`,
        {
          method: "POST",
          body: { target_location_id: targetId },
        }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.lists(),
      })
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.details(),
      })
    },
  })
}

export type RestoreLocationPreview = {
  location: { id: string; name: string; deleted_at: string }
  seller_link: { seller_id: string; deleted_at: string | null } | null
  facility_link: { facility_profile_id: string; deleted_at: string | null } | null
  inventory_levels: number
  orders_fulfilled: number
}

export const useRestoreLocationPreview = (locationId: string, enabled: boolean) => {
  return useQuery<RestoreLocationPreview, FetchError, RestoreLocationPreview>({
    queryKey: [...stockLocationsQueryKeys.all, "restore-preview", locationId],
    queryFn: () =>
      sdk.client.fetch(`/admin/stock-locations/${locationId}/restore`, {
        method: "GET",
      }),
    enabled: enabled && !!locationId,
    retry: false,
  })
}

export const useRestoreLocation = () => {
  return useMutation({
    mutationFn: ({ locationId }: { locationId: string }) =>
      sdk.client.fetch<{ success: boolean; location: { id: string; name: string }; restored: string[] }>(
        `/admin/stock-locations/${locationId}/restore`,
        { method: "POST" }
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.lists(),
      })
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.details(),
      })
      await queryClient.invalidateQueries({
        queryKey: [...stockLocationsQueryKeys.all, "recently-deleted"],
      })
    },
  })
}

export type DeletePreview = {
  location: { id: string; name: string }
  seller: { id: string; name: string } | null
  facility_profile: { id: string; slug: string } | null
  impact: {
    inventory_levels: number
    sessions_with_enrollment: number
    orders_fulfilled: number
    fulfillment_sets: number
    service_zones: number
    shipping_options: number
  }
  can_delete: boolean
  block_reasons: string[]
}

export const useDeleteLocationPreview = (locationId: string, enabled: boolean) => {
  return useQuery<DeletePreview, FetchError, DeletePreview>({
    queryKey: [...stockLocationsQueryKeys.all, "delete-preview", locationId],
    queryFn: () =>
      sdk.client.fetch(`/admin/stock-locations/${locationId}/delete-preview`, {
        method: "GET",
      }),
    enabled: enabled && !!locationId,
    retry: false,
  })
}

export type RecentlyDeletedLocation = {
  id: string
  name: string
  deleted_at: string
  seller: { id: string; name: string } | null
  inventory_levels: number
  orders_fulfilled: number
  has_data: boolean
}

export const useRecentlyDeletedLocations = (enabled: boolean) => {
  return useQuery<{ locations: RecentlyDeletedLocation[]; count: number }, FetchError>({
    queryKey: [...stockLocationsQueryKeys.all, "recently-deleted"],
    queryFn: () =>
      sdk.client.fetch("/admin/stock-locations/recently-deleted", {
        method: "GET",
      }),
    enabled,
  })
}

export const useUpdateStockLocationFulfillmentProviders = (
  id: string,
  options?: UseMutationOptions<
    HttpTypes.AdminStockLocationResponse,
    FetchError,
    HttpTypes.AdminBatchLink
  >
) => {
  return useMutation({
    mutationFn: async (payload) =>
      await sdk.admin.stockLocation.updateFulfillmentProviders(id, payload),
    onSuccess: async (data, variables, context, _ctx) => {
      await queryClient.invalidateQueries({
        queryKey: stockLocationsQueryKeys.details(),
      })
      await queryClient.invalidateQueries({
        queryKey: fulfillmentProvidersQueryKeys.all,
      })

      options?.onSuccess?.(data, variables, context, _ctx)
    },
    ...options,
  })
}
